import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How do you choose which casinos to review?',
      answer: 'We only review casinos that are licensed by the UK Gambling Commission (UKGC). We prioritize casinos with strong reputations, good player feedback, competitive bonuses, and a wide selection of games. All casinos we review must meet our strict standards for security, fairness, and customer service.'
    },
    {
      question: 'Are your reviews biased?',
      answer: 'No. We maintain complete editorial independence. While we may receive affiliate commissions when players sign up through our links, this never influences our reviews. We test each casino thoroughly and provide honest, unbiased assessments. Negative reviews are just as important to us as positive ones.'
    },
    {
      question: 'How often are reviews updated?',
      answer: 'We regularly update our reviews to reflect changes in bonuses, game selections, terms and conditions, and overall casino performance. Major updates are typically done quarterly, but we also make immediate updates when significant changes occur.'
    },
    {
      question: 'What makes a casino "top-rated" on your site?',
      answer: 'Our top-rated casinos excel in multiple areas: game variety and quality, bonus value and terms, customer support responsiveness, withdrawal speed, security measures, and overall user experience. They must maintain consistently high ratings across all these categories.'
    },
    {
      question: 'Do you test withdrawals yourself?',
      answer: 'Yes, when possible. We test the withdrawal process for each casino we review, noting processing times, any fees, and the overall ease of the process. However, we also rely on verified player feedback and industry data to provide comprehensive information.'
    },
    {
      question: 'Can I trust the bonus information?',
      answer: 'We verify all bonus information directly from the casino\'s website and terms and conditions. However, bonuses can change frequently, so we always recommend checking the casino\'s current offers before signing up. We update our information regularly to keep it current.'
    },
    {
      question: 'What should I look for in a casino bonus?',
      answer: 'Key factors include: the bonus amount, wagering requirements (lower is better), maximum bet limits, game restrictions, time limits, and withdrawal conditions. A smaller bonus with better terms is often more valuable than a larger bonus with restrictive conditions.'
    },
    {
      question: 'How do I know if a casino is safe?',
      answer: 'Look for UKGC licensing, SSL encryption (indicated by HTTPS), positive player reviews, clear terms and conditions, and responsible gaming tools. All casinos we review meet these safety standards. We also check for any history of complaints or regulatory issues.'
    },
    {
      question: 'What payment methods are typically available?',
      answer: 'Most UK casinos accept credit/debit cards (Visa, Mastercard), e-wallets (PayPal, Skrill, Neteller), and bank transfers. Some also accept cryptocurrencies. Processing times vary: e-wallets are usually fastest (instant to 24 hours), while bank transfers can take 3-5 business days.'
    },
    {
      question: 'Can I play at these casinos from outside the UK?',
      answer: 'This depends on each casino\'s terms and your location. UKGC-licensed casinos are primarily for UK residents. If you\'re outside the UK, check the casino\'s terms to see if they accept players from your country. Some casinos have separate licenses for other jurisdictions.'
    },
    {
      question: 'What is responsible gambling?',
      answer: 'Responsible gambling means playing within your means, setting limits on time and money spent, never chasing losses, and recognizing when to take a break. All UKGC-licensed casinos must provide tools like deposit limits, self-exclusion, and reality checks to help players gamble responsibly.'
    },
    {
      question: 'How can I contact you?',
      answer: 'You can reach us through our contact form or email. We welcome feedback, questions, and suggestions. If you\'ve had a particularly good or bad experience with a casino we\'ve reviewed, we\'d love to hear about it as it helps us keep our reviews accurate and up-to-date.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions - UK Casino Reviews"
        description="Get answers to common questions about UK online casinos, bonuses, safety, and how we review casinos. Your guide to making informed gaming decisions."
        schema={schema}
      />

      <div className="pt-20 pb-12">
        {/* Hero */}
        <section className="py-12 bg-gradient-to-br from-dark-surface to-dark-bg">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-4">
              <span className="text-gradient">Frequently Asked Questions</span>
            </h1>
            <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto">
              Everything you need to know about UK online casinos and our review process
            </p>
          </div>
        </section>

        {/* FAQ Items */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="glass rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-dark-hover transition-colors text-left"
                  >
                    <span className="font-bold text-lg pr-4">{faq.question}</span>
                    {openIndex === index ? (
                      <FaChevronUp className="text-accent-primary flex-shrink-0" />
                    ) : (
                      <FaChevronDown className="text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 py-4 border-t border-dark-border">
                      <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Help */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient">Still Have Questions?</span>
            </h2>
            <p className="text-gray-400 mb-8">
              Can't find what you're looking for? Check out our detailed casino reviews or contact us directly. 
              We're here to help you make informed decisions about online gaming.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/comparison"
                className="bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all"
              >
                Compare Casinos
              </a>
              <a
                href="/about"
                className="glass px-6 py-3 rounded-lg hover:bg-accent-primary hover:text-white transition-all font-semibold"
              >
                Learn More About Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;

